import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { RootState, useAppDispatch } from "../store";
import { showModal } from "../store/actions/modalActions";
import { LicensePlan, accessAiFeatures, accessSections } from "../store/actions/PlanActions";
import {
  ROUTE_TO_SECTION,
  UPGRADE_PATH,
  aiFeatureLabel,
  sectionLabel,
} from "../config/entitlementKeys";

/* ─────────────────────────────────────────────────────────
   LICENSING ENFORCEMENT - reads from env var REACT_APP_LICENSING_ENABLED.
   Set to "true" to enable license enforcement on the frontend.
   Defaults to false (pilot mode - all users get full access).
   ───────────────────────────────────────────────────────── */
const LICENSING_ENABLED = process.env.REACT_APP_LICENSING_ENABLED === "true";

/*
  THE KEYS COME FROM ONE PLACE - `config/entitlementKeys`.

  These three maps used to live here as literals, and they had drifted from
  the grants they are compared against: `/contacts` asked for `clients` and
  `/reviews` asked for `review`, both renamed across the whole catalogue by
  backend Story 1205. The comparison is a bare `includes()`, so a stale key
  does not error - it just answers "no", and an org on PONS Full found
  Contacts and Reviews locked behind an upgrade wall for the plan it was
  already paying for.
*/

/* ─────────────────────────────────────────────────────────
   Find which plan/addon unlocks a given section or feature
   ───────────────────────────────────────────────────────── */
function findUnlockingPlan(
  section: string | null,
  aiFeature: string | null,
  plans: LicensePlan[],
  addons: LicensePlan[]
): { plan: LicensePlan | null; isAddon: boolean } {
  // Check addons first (they're the targeted unlock for specific sections)
  for (const addon of addons) {
    if (
      (section && addon.feature_gates.sections.includes(section)) ||
      (aiFeature && addon.feature_gates.ai_features.includes(aiFeature))
    ) {
      return { plan: addon, isAddon: true };
    }
  }
  // Check base plans (higher tiers unlock more sections)
  for (const plan of plans) {
    if (
      (section && plan.feature_gates.sections.includes(section)) ||
      (aiFeature && plan.feature_gates.ai_features.includes(aiFeature))
    ) {
      return { plan, isAddon: false };
    }
  }
  return { plan: null, isAddon: false };
}

/** Stable empty fallback for the plan/addon selectors - see below. */
const NO_PLANS: LicensePlan[] = [];

/* ─────────────────────────────────────────────────────────
   Hook
   ───────────────────────────────────────────────────────── */
export function useLicenseGate() {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const access = useSelector((state: RootState) => state.license?.access);
  // A shared frozen empty array, so the fallback branch returns the SAME
  // reference every time - a `?? []` inside a selector makes react-redux see a
  // changed result on every store notification and re-render the subscriber.
  const plans = useSelector((state: RootState) => state.license?.plans ?? NO_PLANS);
  const addons = useSelector((state: RootState) => state.license?.addons ?? NO_PLANS);
  const loaded = useSelector((state: RootState) => state.license?.loaded ?? false);

  /*
    THESE THREE MUST BE MEMOIZED.

    `?? []` / `?? {}` mints a fresh literal on every render whenever the
    license payload is absent or partial - which is the normal state while the
    license call is in flight, and the permanent state wherever licensing is
    not provisioned. That fresh identity flowed into the `useCallback` deps
    below, so `hasAiFeatureAccess` / `hasSectionAccess` were new functions on
    every render. Any effect depending on one of them re-ran every render; if
    it also set state (MatterDetails' data-sources effect did) the two fed
    each other and the fetch looped without bound.
  */
  /* `accessSections` / `accessAiFeatures` are THE readers - they take the
     canonical top-level list and fall back to the nested one, so no gate is
     keyed on a field name the normaliser might stop writing. */
  const licensedSections = useMemo(() => accessSections(access), [access]);
  const licensedAiFeatures = useMemo(() => accessAiFeatures(access), [access]);
  const hasBaseSeat = access?.has_base_seat ?? true;
  const planType = access?.license_info?.plan_type ?? null;
  const planName = access?.license_info?.plan_name ?? null;

  // Only PONS Starter supports add-ons
  const isAddonEligible = planName === "pons_starter";
  const isTrail = planType === "trial";
  const limits = useMemo(
    () => access?.effective_features?.limits ?? {},
    [access]
  );

  /*
    AN EMPTY ENTITLEMENT SET IS "NOT YET", NEVER "NO".

    Every plan grants at least `dashboard`, so there is no licensed org with
    zero sections. An empty list therefore means the payload is one we should
    not have believed - a bootstrap race before the session cookie exists, a
    partial body - and reading it as a denial is what put "your plan does not
    include Matters" in front of a firm holding all 21 sections on PONS Full.

    Failing OPEN here is the right way round: the gate is UX, and the server
    enforces regardless. A page briefly shown that the API then refuses costs
    one error; the whole product locked behind an upgrade wall costs the
    customer's confidence in it.
  */
  const entitlementsUsable = loaded && !!access && licensedSections.length > 0;

  /** Check if the org has access to a given section */
  const hasSectionAccess = useCallback(
    (section: string): boolean => {
      if (!LICENSING_ENABLED) return true; // Pilot mode - all access granted
      if (!entitlementsUsable) return true; // Not loaded, or not believable
      return licensedSections.includes(section);
    },
    [entitlementsUsable, licensedSections]
  );

  /** Check if the org has access to a given AI feature */
  const hasAiFeatureAccess = useCallback(
    (feature: string): boolean => {
      if (!LICENSING_ENABLED) return true;
      /* Sections, not AI features, decide believability: a real plan can hold
         zero AI features (Starter does), so an empty AI list is a fact. An
         empty SECTION list is not. */
      if (!entitlementsUsable) return true;
      return licensedAiFeatures.includes(feature);
    },
    [entitlementsUsable, licensedAiFeatures]
  );

  /** Resolve route path to license section name */
  const sectionForRoute = useCallback((route: string): string | null => {
    // Try exact match first
    if (ROUTE_TO_SECTION[route]) return ROUTE_TO_SECTION[route];
    // Try prefix match (e.g. /matters/123 → matters)
    const basePath = "/" + route.split("/").filter(Boolean)[0];
    return ROUTE_TO_SECTION[basePath] ?? null;
  }, []);

  /**
   * Gate a create action by section name.
   * Returns `true` if the action is allowed, `false` if blocked (modal shown).
   */
  const checkSectionGate = useCallback(
    (section: string): boolean => {
      if (!LICENSING_ENABLED) return true; // Pilot mode - all access granted
      if (!entitlementsUsable) return true;
      if (licensedSections.includes(section)) return true;

      const { plan, isAddon } = findUnlockingPlan(section, null, plans, addons);
      const label = sectionLabel(section);

      // Tier-aware messaging
      let subMsg: string;
      if (isTrail) {
        // Trail users: tell them to upgrade to Starter or Full
        subMsg = `Your trial does not include <strong>${label}</strong>. Upgrade to <strong>PONS Starter</strong> or <strong>PONS Full</strong> to unlock this feature.`;
      } else if (isAddonEligible && isAddon && plan) {
        // Starter users: suggest the specific add-on
        subMsg = `Your current plan does not include <strong>${label}</strong>. Add the <strong>${plan.display_name}</strong> add-on to unlock this feature, or request it from your admin.`;
      } else if (plan) {
        subMsg = `Your current plan does not include <strong>${label}</strong>. Upgrade to <strong>${plan.display_name}</strong> to unlock this feature.`;
      } else {
        subMsg = `Your current plan does not include access to <strong>${label}</strong>. Please upgrade your plan.`;
      }

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: "Upgrade Required",
          subMsg,
          title: label,
          content: plan ? plan.display_name : undefined,
          inputData: section,
          category: isAddon ? "addon" : "base",
          onConfirm: () => {
            history.push(UPGRADE_PATH);
          },
        })
      );
      return false;
    },
    [entitlementsUsable, licensedSections, plans, addons, dispatch, history, isTrail, isAddonEligible]
  );

  /**
   * Gate a create action by AI feature name.
   * Returns `true` if the action is allowed, `false` if blocked (modal shown).
   */
  const checkAiFeatureGate = useCallback(
    (feature: string): boolean => {
      if (!LICENSING_ENABLED) return true; // Pilot mode - all access granted
      if (!entitlementsUsable) return true;
      if (licensedAiFeatures.includes(feature)) return true;

      const { plan, isAddon } = findUnlockingPlan(null, feature, plans, addons);
      const featureLabel = aiFeatureLabel(feature);

      dispatch(
        showModal({
          type: "license-request",
          showModal: true,
          message: "Upgrade Required",
          subMsg: `Your current plan does not include <strong>${featureLabel}</strong>. ${
            isAddon && plan
              ? `Add the <strong>${plan.display_name}</strong> add-on to unlock this capability.`
              : plan
              ? `Upgrade to <strong>${plan.display_name}</strong> to unlock this capability.`
              : "Please upgrade your plan to access this capability."
          }`,
          title: featureLabel,
          content: plan ? plan.display_name : undefined,
          inputData: feature,
          category: isAddon ? "addon" : "base",
          onConfirm: () => {
            history.push(UPGRADE_PATH);
          },
        })
      );
      return false;
    },
    [entitlementsUsable, licensedAiFeatures, plans, addons, dispatch, history]
  );

  /**
   * Gate a create action for a given route path.
   * Convenience wrapper that resolves route → section → gate.
   */
  const checkRouteGate = useCallback(
    (route: string): boolean => {
      const section = sectionForRoute(route);
      if (!section) return true; // Unknown section - allow
      return checkSectionGate(section);
    },
    [sectionForRoute, checkSectionGate]
  );

  /**
   * Check if a Trail usage limit would be exceeded.
   * Pass the current count and the limit key (e.g. "max_matters").
   * Returns true if allowed, false if at/over limit (modal shown).
   */
  const checkTrailLimit = useCallback(
    (limitKey: string, currentCount: number, resourceLabel?: string): boolean => {
      if (!LICENSING_ENABLED) return true;
      if (!isTrail) return true; // Only Trail plans have hard limits

      const limitValue = limits[limitKey];
      if (limitValue === null || limitValue === undefined) return true; // No limit

      if (currentCount >= limitValue) {
        const label = resourceLabel || limitKey.replace("max_", "").replace("_", " ");
        dispatch(
          showModal({
            type: "license-request",
            showModal: true,
            message: "Trial Limit Reached",
            subMsg: `You've reached your trial limit for <strong>${label}</strong> (${currentCount}/${limitValue}). Upgrade to <strong>PONS Starter</strong> or <strong>PONS Full</strong> to continue.`,
            title: `${label} Limit`,
            category: "base",
            onConfirm: () => {
              history.push(UPGRADE_PATH);
            },
          })
        );
        return false;
      }
      return true;
    },
    [isTrail, limits, dispatch, history]
  );

  return {
    loaded,
    hasBaseSeat,
    hasSectionAccess,
    hasAiFeatureAccess,
    checkSectionGate,
    checkAiFeatureGate,
    checkRouteGate,
    checkTrailLimit,
    sectionForRoute,
    licensedSections,
    licensedAiFeatures,
    isTrail,
    isAddonEligible,
    planType,
    planName,
    limits,
  };
}
